import React, { useState } from 'react';

import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { ButtonWithPopOut, constants, typographyStyles } from '@newsela/angelou';

import Icon from '@client/common/components/Icon';

import image from './NoResultsReview.png';
import { $listingTitle, $noStandardsToReview, $reviewHeaderContainer, $sortOptions, $standardsContainer } from './styles';
import StandardCard from '../StandardCard/StandardCard';
import { STANDARD_APPLIED, STANDARD_SELECTED, TABS } from '../StandardsSelector';

const SORT_OPTIONS = {
  STATE_ASC: 'STATE_ASC',
  STATE_DESC: 'STATE_DESC',
  YEAR_ASC: 'YEAR_ASC',
  YEAR_DESC: 'YEAR_DESC',
};

function getRegion (standard) {
  const isGraphQLResponse = standard.__typename;
  if (!isGraphQLResponse) {
    if (Array.isArray(standard.regions)) return standard.regions[0] || '';
    return standard.regions || '';
  }
  return standard.region || '';
}

function getYearAdopted (standard) {
  const isgraphQLResponse = standard.__typename;
  if (!isgraphQLResponse) {
    return standard.adoption_year || 0;
  }
  return standard.yearAdopted || 0;
}

const StandardsList = ({
  standards,
  isSearching,
  setSelectedStandards,
  selectedStandards,
  activeTab,
  categorizedStandards = [],
  setAppliedStandards,
  onBackToRecommendations
}) => {
  const isReviewing = activeTab === TABS.REVIEW;

  const [newlySelectedSortOption, setNewlySelectedSortOption] = useState(SORT_OPTIONS.STATE_ASC);
  const [appliedSortOption, setAppliedSortOption] = useState(SORT_OPTIONS.STATE_ASC);

  const sortStandards = (arr, sortOption) => {
    const sorted = [...arr];
    switch (sortOption) {
      case SORT_OPTIONS.STATE_ASC:
        return sorted.sort((a, b) => getRegion(a).localeCompare(getRegion(b)));
      case SORT_OPTIONS.STATE_DESC:
        return sorted.sort((a, b) => getRegion(b).localeCompare(getRegion(a)));
      case SORT_OPTIONS.YEAR_ASC:
        return sorted.sort((a, b) => (parseInt(getYearAdopted(a)) || 0) - (parseInt(getYearAdopted(b)) || 0));
      case SORT_OPTIONS.YEAR_DESC:
        return sorted.sort((a, b) => (parseInt(getYearAdopted(b)) || 0) - (parseInt(getYearAdopted(a)) || 0));
      default:
        return arr;
    }
  };

  const renderStandardsGroup = (title, standardsGroup, sortOption, onSortChange) => {
    const isSelectedGroup = title.includes('Selected');
    const isAppliedGroup = title.includes('Applied');

    const hasAppliedStandards = categorizedStandards.some((s) => s.__temporaryStatus === STANDARD_APPLIED);
    const hasSelectedStandards = categorizedStandards.some((s) => s.__temporaryStatus === STANDARD_SELECTED);

    // An empty state is shown for the user when he is first introducing new standards.
    const shouldSkipAppliedGroup = isAppliedGroup && !hasAppliedStandards;
    const shouldSkipSelectedGroup = isSelectedGroup && hasAppliedStandards && !hasSelectedStandards;
    const sortedGroup = sortStandards(standardsGroup, sortOption);
    const shouldShowEmptyState = isSelectedGroup && !hasAppliedStandards && sortedGroup.length === 0;

    if (shouldSkipAppliedGroup || shouldSkipSelectedGroup) {
      return null;
    }

    return (
      <div>
        <div css={$reviewHeaderContainer}>
          <p css={$listingTitle}>
            {title}{standardsGroup.length}
          </p>
          <div css={{ position: 'relative' }}>
            <ButtonWithPopOut
              __cssFor={{
                buttonContents: { color: constants.colors.ui.core[700] },
              }}
              closeOnBlur
              buttonContents='Sort'
              ariaProps={{ 'aria-label': `Sort ${title}` }}
              popOutContents={
                <div css={$sortOptions}>
                  <div onClick={() => onSortChange(SORT_OPTIONS.STATE_ASC)}>State name (Ascending)</div>
                  <div onClick={() => onSortChange(SORT_OPTIONS.STATE_DESC)}>State name (Descending)</div>
                  <div onClick={() => onSortChange(SORT_OPTIONS.YEAR_ASC)}>Adoption year (Ascending)</div>
                  <div onClick={() => onSortChange(SORT_OPTIONS.YEAR_DESC)}>Adoption year (Descending)</div>
                </div>
            }
              id={`sort-${title}`}
            />
          </div>
        </div>

        {shouldShowEmptyState && (
          <div css={$noStandardsToReview}>
            <img src={image} alt='No results found' />
            <p>Select standards in order to review and apply standards</p>
          </div>
        )}

        {sortedGroup.length > 0 &&
        sortedGroup.map((standard) => (
          <StandardCard
            key={standard.id}
            standard={standard}
            setSelectedStandards={setSelectedStandards}
            selectedStandards={categorizedStandards}
            isReviewing={isReviewing}
            setAppliedStandards={setAppliedStandards}
          />
        ))}
      </div>
    );
  };

  if (isReviewing) {
    const appliedStandards = categorizedStandards.filter((s) => s.__temporaryStatus === STANDARD_APPLIED);
    const newlySelectedStandards = categorizedStandards.filter((s) => s.__temporaryStatus === STANDARD_SELECTED);

    return (
      <div css={$standardsContainer(isReviewing)}>
        {renderStandardsGroup('Newly Selected Standards: ', newlySelectedStandards, newlySelectedSortOption, setNewlySelectedSortOption)}
        {renderStandardsGroup('Applied Standards: ', appliedStandards, appliedSortOption, setAppliedSortOption)}
      </div>
    );
  }

  const renderListHeader = () => {
    return (
      <div css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'

      }}
      >

        <p css={$listingTitle}>
          {isSearching
            ? `Search results: ${standards.length || 0}`
            : 'Recommended Standards'}
        </p>

        {isSearching && (
          <p
            css={{
              ...typographyStyles.textMObj,
              fontWeight: 400,
              color: constants.colors.ui.core[700],
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={onBackToRecommendations}
            tabIndex={0}
            role='button'
            onKeyDown={(e) => e.key === 'Enter' && onBackToRecommendations()}
          >
            <Icon
              icon={faChevronLeft} size={16} color={constants.colors.ui.core[700]} customCss={{
                marginRight: '10px',
              }}
            />
            Back to recommendations
          </p>
        )}
      </div>
    );
  };

  // Handles the Search and Recommendation listing.
  return (
    <div css={$standardsContainer(isReviewing)}>
      {renderListHeader()}

      {standards?.map((standard) => (
        <StandardCard
          key={standard.id}
          standard={standard}
          setSelectedStandards={setSelectedStandards}
          selectedStandards={selectedStandards}
          setAppliedStandards={setAppliedStandards}
        />
      ))}
    </div>
  );
};

export default StandardsList;
